import dayjs from 'dayjs';

import { ChatParticipantReadStatusDto, UserShortDto } from '@/api/generated';
import { personFullName } from '@/components/ui/userAvatar/userAvatar.util.ts';
import { chatDayLabel, ChatTimelineMessage } from '@/views/chats/utils/chatTimeline.util.ts';

export type ChatMessageReadState = {
  readers: ChatReader[];
  waitingFor: UserShortDto[];
};

export type ChatReader = {
  lastReadAt: string;
  person: UserShortDto;
  readAt?: string;
};

export type ChatReadReceipts = {
  readStateOf: (message: ChatTimelineMessage) => ChatMessageReadState;
  stoppedAt: Map<number, ChatReader[]>;
};

export const hasReadUpTo = (lastReadAt: string | undefined, createdAt: string): boolean =>
  lastReadAt !== undefined && !dayjs(lastReadAt).isBefore(createdAt);

export const buildChatReadReceipts = (params: {
  currentUserId: string;
  messages: ChatTimelineMessage[];
  participants: UserShortDto[];
  readStatuses: ChatParticipantReadStatusDto[];
}): ChatReadReceipts => {
  const { currentUserId, messages, participants, readStatuses } = params;
  const others = participants
    .filter(person => person.userId !== currentUserId)
    .toSorted((a, b) => personFullName(a).localeCompare(personFullName(b), 'pl'));
  const lastReadAtOf = new Map(readStatuses.map(status => [status.userId, status.lastReadAt]));
  const readAtOf = new Map(readStatuses.map(status => [status.userId, status.readAt]));
  const readers = others.flatMap(person => {
    const lastReadAt = lastReadAtOf.get(person.userId);
    return lastReadAt
      ? [{ lastReadAt, person, readAt: readAtOf.get(person.userId) ?? undefined }]
      : [];
  });
  const delivered = messages.filter(message => !message.isPending);

  const stoppedAt = new Map<number, ChatReader[]>();
  const lastReadMessageIdOf = new Map<string, number>();
  for (const reader of readers) {
    const lastRead = delivered.findLast(message =>
      hasReadUpTo(reader.lastReadAt, message.createdAt),
    );
    if (!lastRead) {
      continue;
    }
    lastReadMessageIdOf.set(reader.person.userId, lastRead.messageId);
    if (lastRead.sender.userId !== reader.person.userId) {
      stoppedAt.set(lastRead.messageId, [...(stoppedAt.get(lastRead.messageId) ?? []), reader]);
    }
  }

  return {
    readStateOf: message => {
      const audience = others.filter(person => person.userId !== message.sender.userId);
      return {
        readers: readers
          .filter(
            reader =>
              reader.person.userId !== message.sender.userId &&
              hasReadUpTo(reader.lastReadAt, message.createdAt),
          )
          .map(reader =>
            lastReadMessageIdOf.get(reader.person.userId) === message.messageId
              ? reader
              : { ...reader, readAt: undefined },
          ),
        waitingFor: audience.filter(
          person => !hasReadUpTo(lastReadAtOf.get(person.userId), message.createdAt),
        ),
      };
    },
    stoppedAt,
  };
};

export const readTimeLabel = (date: string): string =>
  `${chatDayLabel(date).toLocaleLowerCase('pl')}, ${dayjs(date).format('HH:mm')}`;
